import * as Immutable from 'immutable';
import { Handler, Result, ok, err, ComplexModel, CollectionModel } from './handler';
import { Path } from './path';
import { ComplexAttribute } from './attribute';
import { SchemaError, ErrorCode } from './schema-error';

export function collection(child: Handler, defaultItem: () => any) {
  return new Collection(child, defaultItem);
}

export function collectionAttrib(child: Handler, defaultItem: () => any, ...path: string[]) {

  const selector = (x: ComplexModel) => x.getIn(path);
  const injector = (data: ComplexModel, newMember: any) => data.setIn(path, newMember);

  const attribute = new ComplexAttribute(
    collection(child, defaultItem),
    selector,
    injector
  );

  return attribute;
}

export class Collection implements Handler {

  constructor(private child: Handler, private defaultItem: () => any) {
  }

  public get(path: Path, data: CollectionModel): Result<string, SchemaError> {

    if (path.current === '_children') {
      return this.child.get(path, data);
    }

    if (path.current === '_count') {
      return ok(data.size.toString());
    }

    const index = parseInt(path.current, 10);
    if (isNaN(index) || index >= data.size || index < 0) {
      return err(new SchemaError(
        ErrorCode.invalidArgumentError,
        `Index ${path.current} is invalid.`));
    }

    const item = data.get(index);
    return this.child.get(path.next(), item);
  }

  public set(path: Path, list: CollectionModel, value: string): Result<CollectionModel, SchemaError> {

    if (path.current === '_count' || path.current === '_children') {
      return err(new SchemaError(ErrorCode.invalidSetElementIsKeyword,
                                 'You cannot set keyword.'));
    }

    const index = parseInt(path.current, 10);
    if (isNaN(index) || index > list.size || index < 0) {
      return err(new SchemaError(
        ErrorCode.invalidArgumentError,
        `Index ${path.current} is invalid.`));
    }

    let item: any = null;
    if (index === list.size) {
      // create new item
      item = Immutable.fromJS(this.defaultItem());
    } else {
      item = list.get(index);
    }

    const modifiedItem = this.child.set(path.next(), item, value);
    if (!modifiedItem.success) {
      return modifiedItem;
    }

    const mutatedList = list.set(index, modifiedItem.value);
    return ok(mutatedList);
  }
}
